/* eslint-disable react/no-unknown-property */
import {
  Dropdown,
  Icon,
  Menu,
  MenuItem,
  Tooltip,
} from '@pypestream/design-system';
import { flexRender, Cell as TableCell } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';

import {
  tableStore,
  TableRow,
  emitOnce,
  getDefaultValueForColumn,
  isCellValueEqualToDefault,
  _Node,
  NodeColumns,
  nodeTypeDependentCells,
} from '../../../stores/table-store';
import { getCommonPinningStyles } from '../utils/get-common-pinning-styles';
import './cell.scss';

interface CellProps {
  cell: TableCell<TableRow, unknown>;
  rowId: string;
  hidden?: boolean;
  errorMessage?: string;
}

const CellComponent = (
  { cell, rowId, hidden, errorMessage }: CellProps,
  ref: React.Ref<HTMLTableCellElement>
) => {
  const cellId = `${rowId}-${cell.column.id}`;

  const currentCellValue = tableStore.getValue(
    rowId,
    cell.column.id as NodeColumns
  );
  const isDisabledCell = tableStore.isCellDisabled(
    rowId,
    cell.column.id as keyof TableRow
  );
  const { nodeType } = tableStore.getRowById(rowId);

  const [contextMenuState, setContextMenuState] = useState<
    'closed' | 'closing' | 'opened'
  >('closed');

  // const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);

  const { getRowIndex, isCellSelected, isSelectedStartCell } = tableStore;
  const index = getRowIndex(rowId);

  const isSelected = isCellSelected(cellId);

  const isStarterCell = isSelectedStartCell(cellId);

  const cellRef = useRef<HTMLTableCellElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => cellRef.current!, [cellRef.current]);

  const isIndexCell = cell.column.id === 'index';

  /**
   * externally reset the final context popover state back to closed to
   * avoid the component destroying itself
   */
  useEffect(() => {
    setTimeout(() => {
      if (contextMenuState === 'closing') {
        setContextMenuState('closed');
      }
    }, 100);
  }, [contextMenuState]);

  // useEffect(() => {
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     // Check for common navigation keys like Tab, arrow keys, Enter
  //     if (
  //       e.key === 'Tab' ||
  //       e.key === 'ArrowUp' ||
  //       e.key === 'ArrowDown' ||
  //       e.key === 'ArrowLeft' ||
  //       e.key === 'ArrowRight'
  //     ) {
  //       setIsKeyboardNavigating(true);
  //     }
  //   };

  //   const handleMouseDown = () => {
  //     setIsKeyboardNavigating(false);
  //   };

  //   document.addEventListener('keydown', handleKeyDown);
  //   document.addEventListener('mousedown', handleMouseDown);

  //   return () => {
  //     document.removeEventListener('keydown', handleKeyDown);
  //     document.removeEventListener('mousedown', handleMouseDown);
  //   };
  // }, []);

  const handleFocus = () => {
    tableStore.deselectAllRows();

    if (tableStore.isKeyboardNav) {
      tableStore.deselectAllCells();
      tableStore.selectCell({
        cellId,
        columnId: cell.column.id as NodeColumns,
        rowId,
      });
      tableStore.setSelectedStartCell({
        cellId,
        columnId: cell.column.id as NodeColumns,
        rowId,
      });
      // console.log('Focused by keyboard!');
      // Apply keyboard-specific logic or styling
    } else {
      // console.log('Focused by mouse click!');
      // Apply mouse-specific logic or styling
    }
  };

  const handleBlur = () => {
    if (tableStore.isKeyboardNav) {
      tableStore.deselectCell(cellId);
      tableStore.clearSelectedStartCell();
    }
  };

  const cellContent = (
    <div
      test-id={`c-editor-table--${cell.column.id}-${index}-${tableStore.isEditing() ? 'edit' : 'view'}-mode-inner`}
      className="c-editor-cell__inner"
      style={{
        position: 'relative',
        zIndex: 1,
        height: '100%',
        display: 'flex',
        pointerEvents:
          isSelected &&
          (tableStore.isMetaKeyPressed === false ||
            (tableStore.isCellBeingEdited &&
              tableStore.isMetaKeyPressed === true))
            ? 'auto'
            : 'none',
      }}
      ref={innerRef}
      id={cellId}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </div>
  );

  const cellContentForRender = errorMessage ? (
    <Tooltip content={errorMessage} placement="top" offset={19}>
      {cellContent}
    </Tooltip>
  ) : (
    cellContent
  );

  if (nodeTypeDependentCells[nodeType].includes(cell.column.id as NodeColumns))
    return (
      <td
        data-hidden={hidden}
        data-disabled
        test-id={`c-editor-table--${cell.column.id}-td-${index}`}
        key={cell.id}
        id={cellId}
        role="gridcell"
        tabIndex={-1}
        data-row-id={rowId}
        data-cell-id={cell.column.id}
        className={`c-editor-table-td c-editor-table-td--cleared c-editor-table-td--${cell.column.id} ${getCommonPinningStyles(cell.column).classNames} ${isStarterCell ? 'is-selection-start' : ''}`}
        style={{
          ...getCommonPinningStyles(cell.column).styles,
          ...(hidden ? { display: 'none' } : {}),
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
      ></td>
    );

  return (
    <td
      data-hidden={hidden}
      test-id={`c-editor-table--${cell.column.id}-td-${index}`}
      key={cell.id}
      ref={cellRef}
      // tabIndex={0}
      id={cellId}
      role="gridcell"
      tabIndex={-1}
      data-row-id={rowId}
      data-cell-id={cell.column.id}
      onMouseDown={(event) => {
        if (
          event.detail >= 2 &&
          !event.metaKey &&
          tableStore.editingCellId !== cellId
        ) {
          event.preventDefault();
        }
      }}
      onClick={(event) => {
        // auto-close any open popovers
        if (contextMenuState !== 'closed') {
          setContextMenuState('closing');
        }

        emitOnce(() => {
          if (!event.shiftKey) {
            if (event.metaKey && isStarterCell) {
              tableStore.clearSelectedStartCell();
              tableStore.deselectCell(cellId);
            } else {
              tableStore.setSelectedStartCell({
                cellId,
                rowId,
                columnId: cell.column.id as NodeColumns,
              });
            }
          }

          if (!isIndexCell) {
            tableStore.deselectAllRows();
          }

          if (event.metaKey && isSelected && !tableStore.isCellBeingEdited) {
            tableStore.deselectCell(cellId);
          } else if (event.metaKey && !isSelected) {
            tableStore.selectCell({
              cellId,
              rowId,
              columnId: cell.column.id as NodeColumns,
            });
          } else if (!event.metaKey) {
            if (isSelected && tableStore.selectedCells.length === 1) return;
            tableStore.deselectAllCells(cellId);

            if (!isSelected && event.shiftKey) {
              tableStore.selectCell({
                cellId,
                rowId,
                columnId: cell.column.id as NodeColumns,
              });
            }

            if (event.shiftKey && tableStore.selectedStartCell) {
              tableStore.updateRangeSelection({
                cellId,
                rowId,
                columnId: cell.column.id as NodeColumns,
              });
            }
          }
        });
      }}
      onDoubleClick={(event) => {
        if (event.metaKey) {
          if (isSelected) {
            tableStore.deselectCell(cellId);
          } else {
            tableStore.selectCell({
              cellId,
              rowId,
              columnId: cell.column.id as NodeColumns,
            });
          }
        } else {
          tableStore.selectCell({
            cellId,
            rowId,
            columnId: cell.column.id as NodeColumns,
          });
          tableStore.deselectAllCells(cellId);

          if (cellRef.current && tableStore.editingCellId !== cellId) {
            tableStore.toggleCellInput(
              cellRef.current,
              rowId,
              cell.column.id as NodeColumns
            );
          }
        }
      }}
      onContextMenu={(e) => {
        if (e.button === 2) {
          if (contextMenuState === 'closed') {
            setContextMenuState('opened');
          }

          e.preventDefault(); // prevent the default behavior when right clicked
          e.stopPropagation();
          tableStore.deselectAllCells(cellId);
          tableStore.deselectAllRows();

          tableStore.setSelectedStartCell({
            cellId,
            columnId: cell.column.id as NodeColumns,
            rowId,
          });

          tableStore.selectCell({
            cellId,
            rowId,
            columnId: cell.column.id as NodeColumns,
          });

          if (
            document.activeElement &&
            'blur' in document.activeElement &&
            typeof document.activeElement.blur === 'function'
          ) {
            document.activeElement?.blur();
          }
        }
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      // available classes: c-editor-table-td--warning c-editor-table-td--error
      className={`c-editor-table-td c-editor-table-td--${cell.column.id} ${getCommonPinningStyles(cell.column).classNames} ${isStarterCell ? 'is-selection-start' : ''} ${isSelected ? 'is-selected' : ''} ${errorMessage ? 'c-editor-table-td--error' : ''}`}
      style={{
        ...getCommonPinningStyles(cell.column).styles,
        ...(hidden ? { display: 'none' } : {}),
      }}
    >
      {cellContentForRender}
      {tableStore.isEditing() && contextMenuState !== 'closed' && (
        <Dropdown
          test-id={`c-editor-table--${cell.column.id}-td-${index}-dropdown`}
          id={`c-editor-table--${cellId}-td-dropdown`}
          key={`c-editor-table--${cellId}-td-dropdown`}
          trigger={cellId}
          placement="left-start"
          skidding={-24}
          keepMounted={false}
          disableTriggerListeners
          onclose={() => {
            /** setting the contextMenuState to 'closing' basically triggers a
             * 100ms timer (via useEffect) to then remove from the DOM
             */
            setContextMenuState('closing');

            if (cellRef.current) {
              cellRef.current.removeAttribute('data-popover-open');
            }

            if (
              document.activeElement &&
              'blur' in document.activeElement &&
              typeof document.activeElement.blur === 'function'
            ) {
              document.activeElement?.blur();
            }
          }}
          active={contextMenuState === 'opened'}
          closeOnEscClick
        >
          <Menu>
            <MenuItem
              onClick={() =>
                tableStore.handleCopy({
                  cellId: cell.column.id as keyof TableRow,
                  rowId,
                })
              }
            >
              <Icon size="small" name="copy" slot="prefix" />
              Copy Value
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (isDisabledCell) return;
                tableStore.handleCut({
                  cellId: cell.column.id as keyof TableRow,
                  rowId,
                });
              }}
              disabled={isDisabledCell}
            >
              <Icon size="small" name="scissors" slot="prefix" />
              Cut Value
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (isDisabledCell) return;
                const hasCell = tableStore.hasCell(rowId, cell.column.id);
                if (!hasCell) return;

                const typedCellId = cell.column.id as NodeColumns;

                tableStore.updateRow({
                  rowId,
                  updates: {
                    [typedCellId]: getDefaultValueForColumn(typedCellId),
                  },
                  skipValidationRules: false,
                  saveImmediately: true,
                });
              }}
              disabled={
                isDisabledCell ||
                isCellValueEqualToDefault(
                  currentCellValue,
                  getDefaultValueForColumn(cell?.column?.id as NodeColumns)
                )
              }
            >
              <Icon size="small" name="eraser" slot="prefix" />
              Clear Value
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (isDisabledCell) return;
                tableStore.handlePaste({
                  cellId: cell.column.id as NodeColumns,
                  rowId,
                });
              }}
              disabled={isDisabledCell}
            >
              <Icon size="small" name="clipboard" slot="prefix" />
              Paste Value
            </MenuItem>
          </Menu>
        </Dropdown>
      )}
    </td>
  );
};

const ForwardedCell = forwardRef(CellComponent);
export const Cell = observer(ForwardedCell);
